import { Button, Form, Modal, Radio, Select } from 'antd';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { UserContext } from 'app/context/UserContext/UserContext';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotels } from 'app/hooks/data/hotels/useHotel';
import { ExperienceType } from 'app/typings/experiences';

import { RULES } from '../../Hotels/HotelDetail/Finance/utils';

import './NewExperienceModal.scss';

const { Option } = Select;

type NewExperienceModalForm = {
  experienceType: ExperienceType;
  hotelId?: number;
  isLegacy?: boolean;
};

type NewExperienceModalProps = {
  open: boolean;
  onClose: () => void;
};

export const NewExperienceModal = ({
  open,
  onClose,
}: NewExperienceModalProps) => {
  const { isSuperAdmin } = useContext(UserContext);
  const { enterViewMode } = useContext(ViewEditModeContext);
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const { data: hotels } = useHotels();
  const history = useHistory();

  const handleOk = async (values: NewExperienceModalForm) => {
    const isOriginal = values.experienceType === ExperienceType.STAYCATION;
    const isLegacy =
      isOriginal && values.isLegacy !== undefined
        ? values.isLegacy
        : hotels?.find((h) => h.id === values.hotelId)?.isLegacy ?? true;

    enterViewMode();

    const url = isLegacy ? '/activities/legacy/new' : '/activities/new';

    history.push({
      pathname: url,
      search: isOriginal ? `?isOriginal=true` : `?hotelId=${values.hotelId}`,
    });
  };

  return (
    <Modal
      title="Create new activity"
      open={open}
      width={572}
      closable
      destroyOnClose
      onCancel={onClose}
      centered
      className="new-experience-modal"
      footer={[
        <Button onClick={onClose}>No</Button>,
        <Button onClick={form.submit} type="primary" loading={false}>
          Yes
        </Button>,
      ]}
    >
      <Form
        onFinish={handleOk}
        form={form}
        initialValues={{ experienceType: ExperienceType.HOTEL }}
        layout="vertical"
      >
        <FormLineLayout label="Activity type" isRequired isInModal>
          <Form.Item name="experienceType" rules={RULES}>
            <Radio.Group>
              <Radio value="HOTEL">Hotel</Radio>
              <Radio value="STAYCATION" disabled={!isSuperAdmin}>
                Staycation (Originals)
              </Radio>
            </Radio.Group>
          </Form.Item>
        </FormLineLayout>
        {formValues && formValues.experienceType === ExperienceType.HOTEL && (
          <FormLineLayout label="Hotel" isRequired isInModal>
            <Form.Item name="hotelId" rules={RULES}>
              <Select
                placeholder="Select a hotel"
                showSearch
                optionFilterProp="children"
              >
                {hotels?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </FormLineLayout>
        )}
        {formValues &&
          formValues.experienceType === ExperienceType.STAYCATION && (
            <FormLineLayout label="Mode" isRequired isInModal>
              <Form.Item name="isLegacy" rules={RULES}>
                <Radio.Group>
                  <Radio value={true}>Old</Radio>
                  <Radio value={false}>New</Radio>
                </Radio.Group>
              </Form.Item>
            </FormLineLayout>
          )}
      </Form>
    </Modal>
  );
};
